import { Component, type ReactNode } from 'react';
import { RotateCw } from 'lucide-react';
import { captureFrontException } from '../lib/sentry';
import { IconButtonWithText } from './IconButton';

/**
 * ErrorBoundary global — catch les exceptions React runtime + render un
 * fallback propre. Capture vers Sentry (no-op si DSN absent).
 *
 * Sprint 16.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    void captureFrontException(error, {
      extra: { componentStack: info.componentStack ?? undefined },
    });
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-ink-950">
          <div className="tc-panel max-w-lg w-full p-8">
            <div className="tc-label mb-3 text-red-400">runtime / fatal</div>
            <h1 className="text-xl font-display tracking-tight mb-2">Application crashed</h1>
            <p className="text-sm text-steel-300 mb-6">
              An unexpected error was reported to the team. Reload the page to retry.
            </p>
            <details className="text-xs text-steel-500 mb-6">
              <summary className="cursor-pointer hover:text-steel-300 font-mono uppercase tracking-widest text-2xs">
                ▸ technical details
              </summary>
              <pre className="mt-3 bg-ink-950 border border-ink-700 p-3 overflow-auto max-h-60 text-2xs text-red-300">
                {this.state.error.message}
                {'\n\n'}
                {this.state.error.stack}
              </pre>
            </details>
            <IconButtonWithText
              icon={RotateCw}
              variant="primary"
              onClick={() => window.location.reload()}
            >
              reload
            </IconButtonWithText>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
